// Features: M004-F0730, M004-F0731, M004-F0732
// Tests: T-M004-0255, T-M004-0256, T-M004-0257
//
// Typing-feedback mirrors of the Ledger's rules (ML001-D013 s2.54, Spec ACCT-BP-003/004/005). The Ledger is always the judge:
// nothing here is authoritative, nothing here ever blocks a value the Ledger would accept, and the value is NEVER trimmed or
// silently corrected. T-M004-0256/0257 prove these against the Spec vectors row for row.

export const PAD_WIDTH = 5;
export const DEFAULT_DUE_DAYS = 30;
export const PREFIX_MAX = 16;
export const ADDRESS_MAX_LINES = 8;
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export const FIELD_NAMES = Object.freeze([
  'issuer_name', 'issuer_address', 'vat_number', 'bank_details', 'payment_terms_text',
  'invoice_prefix', 'invoice_due_days', 'bookkeeper_email', 'support_email',
]);

// ---- prefix (ACCT-BP-003) -------------------------------------------------------------------------------------------
export const PREFIX_MESSAGES = Object.freeze({
  empty: 'Enter a prefix of 1 to 16 characters',
  leading_space: 'Spaces are not allowed',
  inner_space: 'Spaces are not allowed',
  trailing_space: 'Remove the space at the end',
  bad_char: 'Use letters, numbers, - or _ only',
  bad_start: 'Start with a letter or number',
  too_long: 'At most 16 characters',
});

// Returns '' when valid, else the Ledger's closed reason (empty, too_long, leading_space, inner_space, trailing_space, bad_char, bad_start).
export function prefixProblem(value) {
  const text = String(value ?? '');
  if (text === '') return 'empty';
  if (text.length > PREFIX_MAX) return 'too_long';
  if (/^\s/.test(text)) return 'leading_space';
  if (/\s$/.test(text)) return 'trailing_space';
  if (/\s/.test(text)) return 'inner_space';
  if (!/^[A-Za-z0-9_-]+$/.test(text)) return 'bad_char';
  if (!/^[A-Za-z0-9]/.test(text)) return 'bad_start';
  return '';
}

export const padSeq = (seq, width = PAD_WIDTH) => String(Math.trunc(Number(seq))).padStart(width, '0');

// "Next invoice number: <prefix><next seq>" (ACCT-BP-004): the typed prefix, or "<org_code>-" when the stored prefix is not set and the
// field is blank. Returns { text, invalid }; invalid means the prefix would be refused.
export function nextInvoiceNumber({ typedPrefix, loadedPrefix, orgCode, nextSeq, padWidth = PAD_WIDTH }) {
  const typed = String(typedPrefix ?? '');
  const unchanged = typed === String(loadedPrefix ?? '');
  if (typed === '' && (loadedPrefix == null || loadedPrefix === '')) {
    return { text: `${orgCode}-${padSeq(nextSeq, padWidth)}`, invalid: false };
  }
  // A stored legacy prefix that breaks the rule is displayed as stored (only an EDITED prefix is checked).
  if (!unchanged && prefixProblem(typed)) return { text: '', invalid: true };
  return { text: `${typed}${padSeq(nextSeq, padWidth)}`, invalid: false };
}

// ---- terms vs due days (ACCT-BP-005) -----------------------------------------------------------------------------
// The first `within N days`, else the first `N days` / `N day` / `N-day`, case-insensitive, N 1..4 digits.
export function termsDays(text) {
  if (text == null) return null;
  const s = String(text);
  const within = /\bwithin\s+(\d{1,4})\s+days?\b/i.exec(s);
  if (within) return Number(within[1]);
  const plain = /\b(\d{1,4})(?:\s+|-)days?\b/i.exec(s);
  return plain ? Number(plain[1]) : null;
}

export const effectiveDueDays = (dueDays) => (dueDays == null || dueDays === '' ? DEFAULT_DUE_DAYS : Number(dueDays));

export function termsMismatch(text, dueDays) {
  const terms = termsDays(text);
  const due = effectiveDueDays(dueDays);
  if (terms == null || terms === due) return null;
  return { terms_days: terms, due_days: due, message: `Payment terms say ${terms} days but invoices are due ${due} days after issue.` };
}

// ---- other field feedback ------------------------------------------------------------------------------------------
export const isBlank = (value) => value == null || String(value).trim() === '';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function fieldProblems(form, loaded) {
  const problems = {};
  if (!isBlank(form.issuer_name) && form.issuer_name.length > 120) problems.issuer_name = 'At most 120 characters';
  const lines = form.issuer_address.filter((line) => !isBlank(line));
  if (lines.length > ADDRESS_MAX_LINES) problems.issuer_address = 'At most 8 lines';
  else if (lines.some((line) => line.length > 120)) problems.issuer_address = 'Each line is at most 120 characters';
  if (!isBlank(form.vat_number) && !/^[A-Za-z0-9 ]{2,20}$/.test(form.vat_number)) problems.vat_number = 'Use 2 to 20 letters, numbers or spaces';
  if (!isBlank(form.bank_details) && form.bank_details.length > 600) problems.bank_details = 'At most 600 characters';
  if (!isBlank(form.payment_terms_text) && form.payment_terms_text.length > 600) problems.payment_terms_text = 'At most 600 characters';
  if (!isBlank(form.invoice_due_days)) {
    const n = Number(form.invoice_due_days);
    if (!/^\d+$/.test(String(form.invoice_due_days).trim()) || n < 1 || n > 365) problems.invoice_due_days = 'Enter a whole number of days from 1 to 365';
  }
  for (const key of ['bookkeeper_email', 'support_email']) {
    const v = form[key];
    if (!isBlank(v) && (v.length > 254 || !EMAIL.test(v))) problems[key] = 'Enter a valid email address';
  }
  // Only an EDITED prefix is checked; a stored legacy value does not block saving other fields.
  if (form.invoice_prefix !== (loaded.invoice_prefix ?? '')) {
    const reason = prefixProblem(form.invoice_prefix);
    if (reason) problems.invoice_prefix = PREFIX_MESSAGES[reason];
  }
  return problems;
}

// ---- form <-> wire ---------------------------------------------------------------------------------------------------
export function profileToForm(profile) {
  return {
    issuer_name: profile.issuer_name ?? '',
    issuer_address: Array.isArray(profile.issuer_address) && profile.issuer_address.length ? [...profile.issuer_address] : [''],
    vat_number: profile.vat_number ?? '',
    bank_details: profile.bank_details ?? '',
    payment_terms_text: profile.payment_terms_text ?? '',
    invoice_prefix: profile.invoice_prefix ?? '',
    invoice_due_days: profile.invoice_due_days == null ? '' : String(profile.invoice_due_days),
    bookkeeper_email: profile.bookkeeper_email ?? '',
    support_email: profile.support_email ?? '',
  };
}

// The wire value of one form field: a blank text field means "not set" and is sent as null (ACCT-BP-002); the prefix is never trimmed.
export function fieldToWire(name, form) {
  const value = form[name];
  if (name === 'issuer_address') {
    const lines = value.filter((line) => !isBlank(line));
    return lines.length ? lines : null;
  }
  if (name === 'invoice_due_days') return isBlank(value) ? null : Number(value);
  if (name === 'invoice_prefix') return value === '' ? null : value;
  return isBlank(value) ? null : value;
}

const sameWire = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Only the CHANGED fields (an untouched field is OMITTED, ACCT-BP-002).
export function buildChanges(form, loadedProfile) {
  const changes = {};
  for (const name of FIELD_NAMES) {
    const next = fieldToWire(name, form);
    if (!sameWire(next, loadedProfile[name])) changes[name] = next;
  }
  return changes;
}

export const changedFieldNames = (form, loadedProfile) => Object.keys(buildChanges(form, loadedProfile));
